import { fetcher } from '../../../lib/api-fetcher';
import { createFileRoute, redirect, useNavigate, useSearch, Link } from '@tanstack/react-router';
import { useMemo } from 'react';
import { getSession } from '../../../lib/session';
import { defineListPage, useListPage } from '@repo/list';
import type { PaginatedQueryFn } from '@repo/list';
import { DataTable, createUrlAdapter } from '@repo/web-list';
import { usePageTitle } from '../../../lib/hooks';
import { StatusBadge } from '../../../components/ui/StatusBadge';
import { Eye } from 'lucide-react';

export interface UserListRow {
  id: string;
  name?: string;
  email?: string;
  phoneNumber?: string;
  isStaff?: boolean;
  isAgent?: boolean;
  isCustomer?: boolean;
  status: 'active' | 'pending_password_reset' | 'pending_activation' | 'suspended';
  createdAt?: string;
}

const fetchUsers: PaginatedQueryFn<UserListRow> = async (params) => {
  const page = parseInt(params.page || '1', 10) || 1;
  const limit = parseInt(params.limit || '20', 10) || 20;

  const searchParams = new URLSearchParams({
    page: String(page),
    limit: String(limit),
  });

  if (params.search) searchParams.set('search', params.search);
  if (params.status) searchParams.set('status', params.status);
  if (params.sort) searchParams.set('sort', params.sort);

  const response = await fetcher.get<{ data: UserListRow[]; meta: { total: number; page: number; limit: number } }>(
    `/staff/v1/users?${searchParams.toString()}`
  );

  return response;
};

import { PageHeader } from '../../../components/layout/PageHeader';

export const Route = createFileRoute('/_auth/users/')({
  beforeLoad: ({ location }) => {
    const session = getSession();
    const isAllowed =
      session?.user.type === 'group_admin' ||
      session?.user.type === 'company_admin' ||
      session?.permissions.includes('user.read');
    if (!isAllowed) {
      throw redirect({ to: '/', search: { callbackUrl: location.href } });
    }
  },
  component: UserDirectoryPage,
});

function UserDirectoryPage() {
  usePageTitle('User Directory');
  const navigate = useNavigate();
  const search = useSearch({ strict: false });

  const usersConfig = useMemo(
    () =>
      defineListPage<UserListRow>({
        variant: 'data-table',
        resource: 'users',
        searchable: true,
        columns: [
          { id: 'name', header: 'Name / Identity', accessor: 'name', sortable: true },
          { id: 'email', header: 'Email Address', accessor: 'email', renderType: 'email' },
          { id: 'phoneNumber', header: 'Mobile Number', accessor: 'phoneNumber', renderType: 'phone' },
          { id: 'surfaces', header: 'Entitlements / Surfaces', accessor: () => null },
          { id: 'status', header: 'Status', accessor: 'status' },
          { id: 'createdAt', header: 'Registered', accessor: 'createdAt', renderType: 'date' },
        ],
        rowActions: [
          {
            label: 'View Identity Details',
            icon: <Eye className="w-4 h-4" />,
            onClick: (row) => navigate({ to: '/users/$id', params: { id: row.id } }),
          },
        ],
        filters: [
          {
            id: 'status',
            label: 'Status',
            type: 'select',
            options: [
              { label: 'Active', value: 'active' },
              { label: 'Pending Activation', value: 'pending_activation' },
              { label: 'Reset Needed', value: 'pending_password_reset' },
              { label: 'Suspended', value: 'suspended' },
            ],
          },
        ],
        defaultPageSize: 20,
      }),
    [navigate],
  );

  const urlAdapter = useMemo(() => createUrlAdapter(search, navigate as never), [search, navigate]);

  const list = useListPage(usersConfig, fetchUsers, urlAdapter);

  return (
    <div className="flex flex-col w-full h-full" style={{ viewTransitionName: 'page-body' }}>
      <PageHeader
        title="User Directory"
        subtitle="Manage system identities, active applications, and platform permissions."
      />

      <DataTable
        config={usersConfig}
        list={list}
        customRenderers={{
          name: (row: UserListRow) => (
            <Link
              to="/users/$id"
              params={{ id: row.id }}
              className="font-medium text-accent-500 hover:text-accent-400 hover:underline"
            >
              {row.name || 'Unnamed Identity'}
            </Link>
          ),
          surfaces: (row: UserListRow) => (
            <div className="flex gap-1.5 items-center">
              {row.isStaff && (
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-accent-50 text-accent-700 border border-accent-200">
                  Staff
                </span>
              )}
              {row.isAgent && (
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-info-bg text-info-text border border-info-200">
                  Agent
                </span>
              )}
              {row.isCustomer && (
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-success-bg text-success-text border border-success-200">
                  Customer
                </span>
              )}
              {!row.isStaff && !row.isAgent && !row.isCustomer && (
                <span className="text-xs text-text-muted italic">No active surfaces</span>
              )}
            </div>
          ),
          status: (row: UserListRow) => {
            const status = row.status;
            let badgeStatus: 'success' | 'warning' | 'danger' | 'info' = 'info';
            let label = 'Unknown';
            if (status === 'active') {
              badgeStatus = 'success';
              label = 'Active';
            } else if (status === 'pending_password_reset') {
              badgeStatus = 'warning';
              label = 'Reset Needed';
            } else if (status === 'pending_activation') {
              badgeStatus = 'info';
              label = 'Pending Activation';
            } else if (status === 'suspended') {
              badgeStatus = 'danger';
              label = 'Suspended';
            }
            return <StatusBadge status={badgeStatus} label={label} />;
          },
        }}
      />
    </div>
  );
}
